import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "./ModalBasicLayout";
import ModalCreateContentFolder from "./ModalCreateContentFolder";
import SelectContentFolder from "../Gallery/SelectContentFolder";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_CANCEL,
  ERROR,
  FIELD_REQUIRED,
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_DESTINATION,
  GALLERY_FOLDERS_MOVE,
  GALLERY_FOLDERS_MOVE_DESCRIPTION,
  GALLERY_FOLDERS_MOVE_ERROR,
  GALLERY_FOLDERS_MOVE_SUCCESS,
  GALLERY_FOLDERS_MOVE_TITLE,
} from "../../i18n/keysTranslations";

const ModalMoveContentsToFolder = ({
  modalOpen = false,
  folders = [],
  currentFolderLabel = null,
  createParentFolderID = null,
  createParentFolderLabel = null,
  disabledFolderID = null,
  isLoading = false,
  isCreatingFolder = false,
  onCloseModal = () => {},
  onCallbackCreate = async () => ({ ok: false }),
  onCallbackMove = async () => ({ ok: false }),
  showResultAlerts = true,
  titleKey = GALLERY_FOLDERS_MOVE_TITLE,
  primaryButtonTextKey = GALLERY_FOLDERS_MOVE,
  descriptionKey = GALLERY_FOLDERS_MOVE_DESCRIPTION,
  successKey = GALLERY_FOLDERS_MOVE_SUCCESS,
  errorKey = GALLERY_FOLDERS_MOVE_ERROR,
  showMoveDescription = true,
}) => {
  const { t } = useTranslationApp();
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      folderID: "",
    },
  });

  useEffect(() => {
    if (modalOpen) reset({ folderID: "" });
  }, [modalOpen, reset]);

  const onCreateFolder = async ({ name, parentFolderID }) => {
    const response = await onCallbackCreate({
      name,
      parentFolderID,
    });

    if (response?.ok && response?.data?.id) {
      setValue("folderID", response.data.id, { shouldValidate: true });
      setIsOpenCreateFolderModal(false);
    }

    return response;
  };

  const onSubmit = async ({ folderID }) => {
    const response = await onCallbackMove({ folderID });

    if (!response?.ok) {
      if (response?.reason === "noChanges") return;

      if (showResultAlerts) {
        await SimpleAlert({
          icon: "error",
          title: t(ERROR),
          text: t(errorKey),
        });
      }
      return;
    }

    if (showResultAlerts) {
      await SimpleAlert({
        icon: "success",
        title: t(successKey),
      });
    }
    onCloseModal();
  };

  const moveDescription = showMoveDescription
    ? currentFolderLabel
      ? t(descriptionKey, { folder: currentFolderLabel })
      : t(descriptionKey, { folder: t(GALLERY_FOLDERS_ALL) })
    : "";

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        title={t(titleKey)}
        description={moveDescription}
        ariaLabel={t(titleKey)}
        maxWidth="sm"
        fullWidth
      >
        <SelectContentFolder
          name="folderID"
          control={control}
          errors={errors}
          folders={folders}
          label={t(GALLERY_FOLDERS_DESTINATION)}
          disabled={isLoading || isCreatingFolder}
          disabledFolderID={disabledFolderID}
          onOpenCreateFolder={() => setIsOpenCreateFolderModal(true)}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
        />
        <DialogActions
          sx={{
            px: {
              xs: 3,
              sm: 5,
            },
            pb: 3,
            mt: 0.5,
          }}
        >
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: onCloseModal,
              disabled: isLoading,
            }}
            propsPrimaryButton={{
              showButton: true,
              text: primaryButtonTextKey,
              onClick: handleSubmit(onSubmit),
              loading: isLoading,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
      <ModalCreateContentFolder
        modalOpen={isOpenCreateFolderModal}
        folders={folders}
        parentFolderID={createParentFolderID}
        parentFolderLabel={createParentFolderLabel}
        isLoading={isCreatingFolder}
        onCloseModal={() => setIsOpenCreateFolderModal(false)}
        onCallbackCreate={onCreateFolder}
      />
    </>
  );
};

export default ModalMoveContentsToFolder;
